import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import { useJsonLd } from '@/hooks/useJsonLd';
import { categories, articles } from '@/mocks/data';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug || 'japanese-culture');

  useEffect(() => {
    if (slug) setActiveCategory(slug);
  }, [slug]);

  const currentCategory = categories.find((c) => c.slug === activeCategory);
  const filteredArticles = articles.filter((a) => a.category === activeCategory);

  const otherCategories = categories.filter((c) => c.slug !== activeCategory);

  const categoryUrl = `https://z3ratu1.cn/category/${activeCategory}`;

  useJsonLd({
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'CollectionPage',
        '@id': categoryUrl,
        url: categoryUrl,
        name: `${currentCategory?.name || ''} - 亚洲一级日本精选内容导航平台`,
        description: currentCategory?.description || '',
        inLanguage: 'zh-CN',
        isPartOf: { '@id': 'https://z3ratu1.cn/#website' },
        breadcrumb: {
          '@type': 'BreadcrumbList',
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              name: '首页',
              item: 'https://z3ratu1.cn/',
            },
            {
              '@type': 'ListItem',
              position: 2,
              name: currentCategory?.name || '',
              item: categoryUrl,
            },
          ],
        },
      },
    ],
  });

  useEffect(() => {
    const catName = currentCategory?.name || '分类';
    document.title = `${catName} - 亚洲一级日本精选内容导航平台`;
  }, [currentCategory]);

  if (!currentCategory) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen pt-24 pb-14">
          <div className="max-w-7xl mx-auto px-4 md:px-6 text-center">
            <h1 className="font-heading text-2xl font-bold text-foreground-900 mb-4">分类未找到</h1>
            <Link to="/" className="text-primary-600 hover:text-primary-700 text-sm">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />
      <main className="min-h-screen pt-24 pb-14">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="mb-6">
            <Breadcrumb items={[
              { label: '分类浏览', href: '#' },
              { label: currentCategory.name },
            ]} />
          </div>

          <div className="mb-10 md:mb-14">
            <div className="flex items-center gap-4 mb-4">
              <div className={`w-12 h-12 md:w-14 md:h-14 flex items-center justify-center rounded-lg ${
                currentCategory.color === 'primary'
                  ? 'bg-primary-100 text-primary-600'
                  : 'bg-accent-100 text-accent-600'
              }`}>
                <i className={`${currentCategory.icon} text-xl`}></i>
              </div>
              <div>
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
                  {currentCategory.name}
                </h1>
                <p className="text-sm text-foreground-500 mt-1">{currentCategory.articleCount} 篇内容</p>
              </div>
            </div>
            <p className="text-sm md:text-base text-foreground-600 leading-relaxed max-w-3xl">
              {currentCategory.description}
            </p>
          </div>

          {/* Category tabs */}
          <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-2 flex-nowrap">
            {categories.map((cat) => (
              <Link
                key={cat.id}
                to={`/category/${cat.slug}`}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 cursor-pointer ${
                  cat.slug === activeCategory
                    ? cat.color === 'primary'
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-accent-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>

          {/* Articles list */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {filteredArticles.map((article) => (
              <Link
                key={article.id}
                to={`/article/${article.id}`}
                className="group block rounded-lg bg-background-50 border border-background-200/70 overflow-hidden hover:border-primary-200 transition-all duration-300 cursor-pointer"
              >
                <div className="relative h-44 md:h-48 overflow-hidden">
                  <img
                    src={article.coverImage}
                    alt={article.title}
                    title={`${article.title} - 亚洲一级日本精选内容`}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                  <div className="absolute top-3 left-3 flex gap-1.5 flex-wrap">
                    {article.tags.slice(0, 2).map((tag) => (
                      <span key={tag} className="px-2.5 py-0.5 rounded-full bg-background-50/90 backdrop-blur-sm text-xs font-medium text-foreground-800 whitespace-nowrap">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="p-4">
                  <h2 className="font-heading font-semibold text-sm text-foreground-900 mb-2 line-clamp-2 leading-snug group-hover:text-primary-600 transition-colors duration-200">
                    {article.title}
                  </h2>
                  <p className="text-xs text-foreground-500 line-clamp-2 leading-relaxed mb-3">
                    {article.excerpt}
                  </p>
                  <div className="flex items-center justify-between text-xs text-foreground-400">
                    <span>{article.publishDate}</span>
                    <span>{article.readTime} 分钟阅读</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {filteredArticles.length === 0 && (
            <div className="text-center py-20">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 rounded-full bg-background-100 text-foreground-400">
                <i className="ri-file-list-3-line text-2xl"></i>
              </div>
              <p className="text-foreground-500 text-sm">该分类暂无内容</p>
            </div>
          )}

          {/* Other categories */}
          <div className="mt-14 md:mt-20">
            <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-6">
              浏览其他分类
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
              {otherCategories.slice(0, 5).map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  className="flex flex-col items-center gap-2 p-4 rounded-lg bg-background-100 hover:bg-background-200 border border-transparent hover:border-background-300/60 transition-all duration-200 cursor-pointer"
                >
                  <div className={`w-10 h-10 flex items-center justify-center rounded-lg ${
                    cat.color === 'primary' ? 'bg-primary-100 text-primary-600' : 'bg-accent-100 text-accent-600'
                  }`}>
                    <i className={`${cat.icon} text-base`}></i>
                  </div>
                  <span className="text-xs font-medium text-foreground-700 whitespace-nowrap">{cat.name}</span>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}